import { Box, Popover } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { FilterIconButton } from '@/features/DataGrid';
import { DimensionFilterEditor, useFiltersParamsContext, useFiltersContext } from '@/features/filters';
import { Analytics } from '@/shared/lib';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import type { SortingItem } from '@/shared/types';
import { AdvancedSortFunctions, SortOptions, SortTypes } from '@/shared/types';
import type { ReducedSortingItem } from '@/shared/ui';
import {
  SortButton,
  getColumnSorting,
  HeaderCell,
  AdvancedSortButton,
} from '@/shared/ui';
import {
  selectConfig,
  selectMetaConfig,
  updateDimensions,
  updateFilters,
  updateSorting,
} from '../../model/slice';

enum ActionType {
  addFilter = 'addFilter',
  clearFilter = 'clearFilter',
  advancedSort = 'advancedSort',
  sort = 'sort',
  sortAscending = 'sortAscending',
  sortDescending = 'sortDescending',
  delete = 'delete',
}

interface Props {
  field: string;
  name: string;
  description?: string;
}

export function DimensionTableHeader({ field, name, description }: Props) {
  const dispatch = useAppDispatch();
  const metaConfig = useAppSelector(selectMetaConfig);
  const { dimensions, metric, sorting, filters } =
    useAppSelector(selectConfig)!;
  const menuRef = useRef<HTMLButtonElement>(null);
  const [activeAction, setActiveAction] = useState<ActionType | null>(null);
  const { dimensionConfigs } = useFiltersParamsContext();
  const { filtersMap } = useFiltersContext();
  const sortingItem = getColumnSorting(sorting, field);
  const isAdvancedSortable = !!metaConfig[metric]?.isAdvancedSortable;
  const menuOptions = useMemo(() => {
    const isFilterAllowed = dimensionConfigs.find(
      (config) => config.id === field,
    );
    const filter = filtersMap[field];

    return [
      ...(isFilterAllowed
        ? [
            {
              label: filter ? 'Edit Filter' : 'Add Filter',
              value: ActionType.addFilter,
            },
          ]
        : []),
      ...(filter
        ? [
            {
              label: 'Clear Filter',
              value: ActionType.clearFilter,
            },
          ]
        : []),
      ...(isAdvancedSortable
        ? [
            {
              label: 'Advanced Sorting',
              value: ActionType.advancedSort,
              selected: sortingItem?.type === SortTypes.ADVANCED,
              nestedOptions: [
                {
                  label: `SUM(${metaConfig[metric]?.name})`,
                  value: metric,
                  selected:
                    sortingItem?.type === SortTypes.ADVANCED &&
                    sortingItem?.function === AdvancedSortFunctions.SUM &&
                    sortingItem?.field === metric,
                  nestedOptions: [
                    {
                      label: 'Ascending',
                      value: `${AdvancedSortFunctions.SUM}\\//\\//${metric}\\//\\//${SortOptions.ASC}`,
                      selected:
                        sortingItem?.type === SortTypes.ADVANCED &&
                        sortingItem?.function === AdvancedSortFunctions.SUM &&
                        sortingItem?.field === metric &&
                        sortingItem?.value === SortOptions.ASC,
                    },
                    {
                      label: 'Descending',
                      value: `${AdvancedSortFunctions.SUM}\\//\\//${metric}\\//\\//${SortOptions.DESC}`,
                      selected:
                        sortingItem?.type === SortTypes.ADVANCED &&
                        sortingItem?.function === AdvancedSortFunctions.SUM &&
                        sortingItem?.field === metric &&
                        sortingItem?.value === SortOptions.DESC,
                    },
                  ],
                },
              ],
            },
          ]
        : []),
      {
        label: 'Sort',
        value: ActionType.sort,
        divider: true,
        selected: sortingItem?.type === SortTypes.SIMPLE,
        nestedOptions: [
          {
            label: 'Ascending',
            value: ActionType.sortAscending,
            selected:
              sortingItem?.type === SortTypes.SIMPLE &&
              sortingItem?.value === SortOptions.ASC,
          },
          {
            label: 'Descending',
            value: ActionType.sortDescending,
            selected:
              sortingItem?.type === SortTypes.SIMPLE &&
              sortingItem?.value === SortOptions.DESC,
          },
        ],
      },
      {
        label: 'Delete',
        value: ActionType.delete,
      },
    ];
  }, [
    sortingItem,
    dimensionConfigs,
    filtersMap,
    field,
    isAdvancedSortable,
    metric,
    metaConfig,
  ]);

  const handleAdvancedSortingUpdate =
    (name: string) => (newSortingItem?: ReducedSortingItem) => {
      const sortingItem = newSortingItem
        ? { ...newSortingItem, name }
        : undefined;

      dispatch(updateSorting({ column: name, item: sortingItem }));
      Analytics.sendFeatureUsage('data_table', 'advanced_sorting');
    };
  const handleSimpleSortingUpdate =
    (name: string) => (newSortOption?: SortOptions) => {
      const sortingItem: SortingItem | undefined = newSortOption
        ? {
            type: SortTypes.SIMPLE,
            name,
            value: newSortOption,
          }
        : undefined;

      dispatch(updateSorting({ column: name, item: sortingItem }));
      Analytics.sendFeatureUsage('data_table', 'simple_sorting');
    };
  const handleActionClose = () => {
    setActiveAction(null);
  };
  const handleAddFilter = () => {
    setActiveAction(ActionType.addFilter);
  };
  const handleClearFilter = () => {
    dispatch(updateFilters(filters.filter((filter) => filter.id !== field)));
  };
  const handleMenuOptionClick = (optionValue: string) => {
    if (optionValue === ActionType.addFilter) {
      handleAddFilter();
    } else if (optionValue === ActionType.clearFilter) {
      handleClearFilter();
    } else if (optionValue === ActionType.sortAscending) {
      handleSimpleSortingUpdate(field)(SortOptions.ASC);
    } else if (optionValue === ActionType.sortDescending) {
      handleSimpleSortingUpdate(field)(SortOptions.DESC);
    } else if (optionValue === ActionType.delete) {
      dispatch(updateDimensions(dimensions.filter((d) => d !== field)));
    } else {
      const composedValue = optionValue.split('\\//\\//');

      if (composedValue.length < 3) {
        return;
      }

      const [advancedSortFunction, advancedSortField, advancedSortOrder] =
        composedValue;

      handleAdvancedSortingUpdate(field)({
        type: SortTypes.ADVANCED,
        value: advancedSortOrder as SortOptions,
        field: advancedSortField,
        function: advancedSortFunction as AdvancedSortFunctions,
      });
    }
  };

  return (
    <>
      <HeaderCell
        field={field}
        title={name}
        ref={menuRef}
        description={description}
        quickActions={(hovered) => {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {(hovered || !!sortingItem) &&
                (isAdvancedSortable ? (
                  <AdvancedSortButton
                    advancedFieldOptions={[
                      {
                        name: metaConfig[metric]?.name,
                        value: metric,
                      },
                    ]}
                    order={sortingItem?.index}
                    advancedField={
                      sortingItem?.type === SortTypes.ADVANCED
                        ? sortingItem.field
                        : undefined
                    }
                    value={sortingItem?.value}
                    onChange={handleAdvancedSortingUpdate(field)}
                  />
                ) : (
                  <SortButton
                    order={sortingItem?.index}
                    value={sortingItem?.value}
                    onChange={handleSimpleSortingUpdate(field)}
                  />
                ))}
              {filtersMap[field] && (
                <FilterIconButton onClick={handleAddFilter} />
              )}
            </Box>
          );
        }}
        menuOptions={menuOptions}
        onMenuOptionClick={handleMenuOptionClick}
      />
      <Popover
        open={activeAction !== null}
        anchorEl={menuRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
            },
          },
        }}
        onClose={handleActionClose}
      >
        {activeAction === ActionType.addFilter && (
          <DimensionFilterEditor id={field} onClose={handleActionClose} />
        )}
      </Popover>
    </>
  );
}
